import { Injectable, inject } from "@angular/core";
import { Store } from "@ngrx/store";
import format from "tz-format";
import { take, tap, mergeMap, publishReplay, refCount } from "rxjs/operators";

import { Model, MedicalDataActions } from "../shared/index";
import { MedicalData, ApplicationState, MedicalDataMap } from "../shared/types";

@Injectable()
export class MedicalDataModel extends Model {
	private readonly store = inject<Store<ApplicationState>>(Store);

	findItem(patient: string, type: string, id: string) {
		return this.store.select((state) => {
			const medData: MedicalDataMap = state.data.medicalData[patient];

			if (!medData || !medData[type]) {
				return null;
			}

			const items = medData[type].items || [];

			return items.find((x) => x.id === id);
		});
	}

	update(patient: string, type: string, id: string, data: MedicalData) {
		const action = MedicalDataActions.update(patient, type, id, data);

		this.store.dispatch(action);

		return this.performAsyncAction(action).pipe(publishReplay(1), refCount());
	}

	save(patient: string, data: MedicalData) {
		if (!data.date) {
			// Add a date if was not yet added by the form itself
			data.date = format(0);
		}

		// Generate the ID
		data.id = `${format(new Date(data.date), 0)}-manual`;
		data.history = [];

		const action = MedicalDataActions.create(patient, data);

		this.store.dispatch(action);

		return this.performAsyncAction(action).pipe(publishReplay(1), refCount());
	}

	loadMedicalData(patient: string) {
		let state: ApplicationState;
		this.store.pipe(take(1)).subscribe((s) => (state = s));

		// @ts-expect-error TS2454
		if (state.data.medicalData[patient] && Object.keys(state.data.medicalData[patient]).length !== 0) {
			this.reload(patient).subscribe({
				error: (err) => {
					console.error(err);
				},
			});

			return;
		}

		this.load(patient).subscribe({
			error: (err) => {
				console.error(err);
			},
		});
	}

	loadMore(patient: string, type: string) {
		return this.store
			.select((state) => state.data.medicalData[patient][type].next)
			.pipe(
				take(1),
				mergeMap((next) => this.load(patient, type, next)),
			);
	}

	// @ts-expect-error TS7006
	private reload(patient) {
		return this.performAsyncAction(MedicalDataActions.load(patient)).pipe(
			tap((result) => {
				const payload = result.payload;
				const types = Object.keys(payload);

				for (const type of types) {
					const paging = payload[type].paging;
					const next = paging && paging.after ? paging.after : undefined;
					const items = payload[type].items;

					this.store.dispatch(MedicalDataActions.set(patient, type, items, next));
				}
			}),
		);
	}

	private load(patient: string, type?: string, next?: string) {
		return this.performAsyncAction(MedicalDataActions.load(patient, type, next)).pipe(
			tap((result) => {
				if (result.payload.items) {
					this.dispatch(patient, type, result.payload);
				} else {
					const types = Object.keys(result.payload);

					for (const type of types) {
						this.dispatch(patient, type, result.payload[type]);
					}
				}
			}),
		);
	}

	// @ts-expect-error TS7006
	private dispatch(patient, type, payload) {
		const items = payload.items;
		const paging = payload.paging;
		const next = paging && paging.after ? paging.after : undefined;

		this.store.dispatch(MedicalDataActions.add(patient, type, items, next));
	}
}
